import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Chip,
    Paper,
    Grid,
    Link,
    Divider,
    Button
} from '@mui/material';
import { Language, Person, Source, Link as LinkIcon, Translate, FindInPage } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import TranslationMonitoringDialog from '../shared/TranslationMonitoringDialog';

const ShortStoryView = ({ story }) => {
    const navigate = useNavigate();
    const [monitoringDialog, setMonitoringDialog] = useState({ open: false, loading: false, data: null });

    const handleTranslateStory = () => {
        if (story?.text?._id) {
            navigate(`/text/translate/${story.text._id}`);
        }
    };

    const handleCheckTranslations = async () => {
        if (!story?.text?._id) return;

        setMonitoringDialog({ open: true, loading: true, data: null });

        try {
            const response = await userRequest.get(`/text/check-translations/${story.text._id}`);
            setMonitoringDialog({ open: true, loading: false, data: response.data });
        } catch (error) {
            console.error('Error checking translations:', error);
            
            // Handle 404 (no translation found) specifically
            if (error.response?.status === 404) {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'No translation found for this story. You need to create a translation first.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: [],
                        noTranslation: true
                    } 
                });
            } else {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'Failed to check translations. Please try again.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: []
                    } 
                });
            }
        }
    };

    const handleCloseMonitoringDialog = () => {
        setMonitoringDialog({ open: false, loading: false, data: null });
    };

    if (!story) {
        return (
            <Box p={3}>
                <Typography variant="body1" color="text.secondary">
                    No story selected
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Grid container spacing={3}>
                {/* Header Section with Thumbnail */}
                <Grid item xs={12} md={story.thumbUrl ? 8 : 12}>
                    <Typography 
                        variant="h4" 
                        gutterBottom 
                        component="div"
                        dangerouslySetInnerHTML={{ __html: story.title }}
                    />
                    
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body1" color="text.secondary">
                                by {story.author}
                            </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                            <Language fontSize="small" color="action" />
                            <Typography variant="body1" color="text.secondary">
                                {story.language?.name || 'Unknown Language'}
                            </Typography>
                        </Box>
                        
                        {story.level?.name && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Level: {story.level.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Tags */}
                    <Box mb={3}>
                        <Typography variant="subtitle2" gutterBottom>
                            Tags:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {story.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    {story.text?._id && (
                        <Box mb={3} display="flex" gap={2} flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Translate />}
                                onClick={handleTranslateStory}
                                size="large"
                            >
                                Open Translation Editor
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<FindInPage />}
                                onClick={handleCheckTranslations}
                                size="large"
                                disabled={!story?.text?._id}
                            >
                                Check Missing Translations
                            </Button>
                        </Box>
                    )}
                </Grid>

                {/* Thumbnail */}
                {story.thumbUrl && (
                    <Grid item xs={12} md={4}>
                        <Paper elevation={2} sx={{ p: 1 }}>
                            <img
                                src={story.thumbUrl}
                                alt={story.title}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '300px',
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </Paper>
                    </Grid>
                )}

                {/* Source Information */}
                {(story.sourceTitle || story.sourceUrl) && (
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Source Information
                        </Typography>
                        
                        {story.sourceTitle && (
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Source fontSize="small" color="action" />
                                <Typography variant="body2">
                                    Source Title: {story.sourceTitle}
                                </Typography>
                            </Box>
                        )}
                        
                        {story.sourceUrl && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <LinkIcon fontSize="small" color="action" />
                                <Link
                                    href={story.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                >
                                    {story.sourceUrl}
                                </Link>
                            </Box>
                        )}
                    </Grid>
                )}

                {/* Story Content */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Story Content
                    </Typography>
                    
                    <Paper elevation={1} sx={{ p: 3, backgroundColor: '#fafafa' }}>
                        {story.text?.text ? (
                            <Box 
                                sx={{ 
                                    fontSize: '1rem',
                                    lineHeight: 1.8, 
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'inherit'
                                }}
                                dangerouslySetInnerHTML={{ __html: story.text.text }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No content available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Metadata */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Metadata
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                Created: {new Date(story.createdAt).toLocaleDateString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                Last Updated: {new Date(story.updatedAt).toLocaleDateString()}
                            </Typography>
                        </Grid>                     
                    </Grid>
                </Grid>
            </Grid>

            {/* Translation Monitoring Dialog */}
            <TranslationMonitoringDialog
                open={monitoringDialog.open}
                onClose={handleCloseMonitoringDialog}
                loading={monitoringDialog.loading}
                data={monitoringDialog.data}
                onFixInEditor={handleTranslateStory}
            />
        </Box>
    );
};

export default ShortStoryView; 